import { BlobReader, BlobWriter, ZipReader, ZipWriter } from "@zip.js/zip.js";
import firebase from "firebase/app";
import uniqid from "uniqid";

import { useCallback, useMemo } from "react";

import contentTypes from "astrid-distribution/src/constants/contentTypes";
import storage from "astrid-firebase/src/storage";
import arrayChunk from "astrid-helpers/src/arrayChunk";
import { isLive } from "astrid-helpers/src/env";

import generateSharePageHtml from "../utils/generateSharePageHtml";

const mimeMap = {
	wav: contentTypes.WAV,
	mp3: contentTypes.MP3,
	epub: contentTypes.EPUB,
	jpg: contentTypes.JPEG,
	jpeg: contentTypes.JPEG,
	pdf: contentTypes.PDF,
	zip: contentTypes.ZIP,
};

const SHAREABLE_PAGE_NAME = "external-share.html";

async function fetchBlob(file, onProgress, signal) {
	const response = await fetch(file.url, { signal });

	if (!response.ok) {
		throw new Error(`Failed to download file from ${file.url}`);
	}

	const contentLength = response.headers.get("content-length");
	if (!contentLength) {
		throw new Error("Missing content length");
	}

	const total = parseInt(contentLength, 10);
	let loaded = 0;
	const reader = response.body.getReader();
	const chunks = [];

	while (true) {
		const { done, value } = await reader.read();
		if (done) break;
		chunks.push(value);
		loaded += value.length;

		onProgress?.(loaded / total);
	}

	return new Blob(chunks);
}

async function fetchStream(file, onProgress, signal) {
	const response = await fetch(file.url, { signal });

	if (!response.ok) {
		throw new Error(`Failed to download file from ${file.url}`);
	}

	const total = parseInt(response.headers.get("content-length"), 10);
	let loaded = 0;

	return response.body.pipeThrough(
		new TransformStream({
			transform(chunk, controller) {
				loaded += chunk.length;

				if (total) {
					onProgress?.(loaded / total);
				}

				controller.enqueue(chunk);
			},
			flush() {
				onProgress?.(1);
			},
		}),
	);
}

// Streams straight to disk where supported (Chromium), otherwise returns null
function pickSaveFile(suggestedName) {
	if (!window.showSaveFilePicker) {
		return null;
	}

	return window.showSaveFilePicker({
		suggestedName,
		types: [{ description: "Zip", accept: { "application/zip": [".zip"] } }],
	});
}

function saveBlob(blob, name) {
	const blobUrl = URL.createObjectURL(blob);
	const a = document.createElement("a");
	a.href = blobUrl;
	a.download = name;
	document.body.appendChild(a);
	a.click();
	document.body.removeChild(a);
	URL.revokeObjectURL(blobUrl);
}

function getUniqueName(name, usedNames) {
	let uniqueName = name;
	let count = 1;

	while (usedNames.has(uniqueName)) {
		const dot = name.lastIndexOf(".");
		uniqueName = dot > 0 ? `${name.slice(0, dot)} (${count})${name.slice(dot)}` : `${name} (${count})`;
		count += 1;
	}

	usedNames.add(uniqueName);

	return uniqueName;
}

export default function useBucket(bucket) {
	const ref = useMemo(() => bucket && storage.refFromURL(`gs://${isLive ? "" : "stage-"}${bucket}`), [bucket]);

	const write = useCallback((file, data, meta) => ref.child(file).put(data, meta), [ref]);

	const putFile = useCallback(
		async (path, sourceFile, onStateChanged, metadata) => {
			console.info(`Uploading file to ${ref.bucket}/${path}`);

			const fileRef = ref.child(path);
			const filePut = fileRef.put(sourceFile, metadata);

			if (onStateChanged) {
				filePut.on(firebase.storage.TaskEvent.STATE_CHANGED, onStateChanged);
			}

			await filePut;

			const [url, metaData] = await Promise.all([fileRef.getDownloadURL(), fileRef.getMetadata()]);

			delete metaData.updated;

			return Object.entries({ url, ...metaData }).reduce((acc, [key, value]) => {
				if (value === undefined) {
					return acc;
				}

				return { ...acc, [key]: value };
			}, {});
		},
		[ref],
	);

	const deleteFile = useCallback(
		async (file) => {
			try {
				console.info(`Deleting file from ${ref.bucket}/${file}`);

				return await ref.child(file?.fullPath || file).delete();
			} catch (error) {
				return false;
			}
		},
		[ref],
	);

	const deleteFiles = useCallback(
		(files) => {
			return Promise.all(files.map((file) => deleteFile(file.fullPath)));
		},
		[ref, deleteFile],
	);

	const deleteFolder = useCallback(
		async (folderId) => {
			try {
				const folderRef = ref.child(folderId);
				const { items, prefixes } = await folderRef.listAll();

				await Promise.all(items.map((itemRef) => itemRef.delete()));
				await Promise.all(prefixes.map((folderRef) => deleteFolder(folderRef.fullPath)));
			} catch (error) {
				throw new Error(`Failed to delete folder ${folderId}: ${error.message}`);
			}
		},
		[ref],
	);

	const unzipAndPutFiles = useCallback(
		async (zip, { folder, onProgress }) => {
			const result = [];

			const zipReader = new ZipReader(new BlobReader(zip));
			const entries = await zipReader.getEntries();
			const progress = {
				[zip.name]: 0,
			};

			const uploadEntries = entries.filter(
				(entry) =>
					!entry.directory &&
					!entry.filename.startsWith(".") &&
					!entry.filename.startsWith("__MACOSX/") &&
					!entry.filename.includes("/__MACOSX/"),
			);
			let completedFiles = 0;
			const totalFiles = uploadEntries.length;

			for (const entry of uploadEntries) {
				const fileName = entry.filename.split("/").pop();
				if (
					entry.directory ||
					fileName.startsWith(".") ||
					fileName.startsWith("__MACOSX/") ||
					fileName.includes("/__MACOSX/")
				) {
					continue;
				}

				const blob = await entry.getData?.(new BlobWriter());
				if (!blob) {
					continue;
				}

				const fileId = uniqid();
				const ext = fileName.split(".").pop();
				const path = `${folder}/${uniqid()}.${ext}`;
				const contentType = mimeMap[ext];
				const unzippedFile = new File([blob], fileName, { type: contentType });

				const onStateChanged = ({ totalBytes, bytesTransferred }) => {
					const currentUploadProgress = bytesTransferred / totalBytes;
					progress[zip.name] = (completedFiles + currentUploadProgress) / totalFiles;
					onProgress({ ...progress });
				};
				const uploadedFile = await putFile(path, unzippedFile, onStateChanged);
				completedFiles += 1;
				result.push({ ...uploadedFile, id: fileId, name: fileName });
			}
			return result;
		},
		[ref, putFile, deleteFiles],
	);

	const putFiles = useCallback(
		async (files, { getPath = (file) => file.name, onProgress }) => {
			const result = [];

			const progress = files.reduce(
				(acc, file) => ({
					...acc,
					[file.name]: 0,
				}),
				{},
			);

			try {
				for (const chunk of arrayChunk(files, 10)) {
					const chunkResult = await Promise.all(
						chunk.map((file) => {
							const path = getPath(file);

							const onStateChanged = ({ totalBytes, bytesTransferred }) => {
								progress[file.name] = bytesTransferred / totalBytes;

								onProgress({ ...progress });
							};

							return putFile(path, file, onStateChanged);
						}),
					);

					result.push(...chunkResult?.flat());
				}
			} catch (error) {
				await deleteFiles(result);

				throw error;
			}

			return result;
		},
		[ref, putFile, deleteFiles],
	);

	const downloadFile = useCallback(async (file, onProgress, signal) => {
		const blob = await fetchBlob(file, onProgress, signal);

		saveBlob(blob, file.name);
	}, []);

	const downloadFiles = useCallback(
		async (files, onProgress, signal) => {
			const progressMap = Object.fromEntries(files.map((f) => [f.name, 0]));

			for (const file of files) {
				await downloadFile(
					file,
					(progress) => {
						progressMap[file.name] = progress;
						onProgress?.({ ...progressMap });
					},
					signal,
				);
			}
		},
		[downloadFile],
	);

	const downloadFilesAsZip = useCallback(async (files, zipName, onProgress, signal) => {
		// Must run before any other await, the browser only opens the picker during the click
		const fileHandle = await pickSaveFile(zipName);
		const fileStream = await fileHandle?.createWritable();

		const progressMap = Object.fromEntries(files.map((f) => [f.name, 0]));
		const zipWriter = new ZipWriter(
			fileStream
				? new WritableStream({
						write: (chunk) => fileStream.write(chunk),
						close: () => fileStream.close(),
				  })
				: new BlobWriter("application/zip"),
		);
		const usedNames = new Set();

		try {
			for (const file of files) {
				const stream = await fetchStream(
					file,
					(progress) => {
						progressMap[file.name] = progress;
						onProgress?.({ ...progressMap });
					},
					signal,
				);

				// Audio and images are already compressed, so store them as is
				await zipWriter.add(getUniqueName(file.name, usedNames), stream, { level: 0, signal });
			}

			const zip = await zipWriter.close();

			if (!fileStream) {
				saveBlob(zip, zipName);
			}
		} catch (error) {
			if (fileStream) {
				// Leave no half written zip behind
				try {
					await fileStream.abort();
					await fileHandle.remove?.();
				} catch (cleanupError) {
					console.warn("Failed to clean up partial zip", cleanupError);
				}
			}

			throw error;
		}
	}, []);

	const downloadFolder = useCallback(
		async (folderId, onProgress, signal) => {
			const folderRef = ref.child(folderId);
			try {
				const { items } = await folderRef.listAll();
				const files = await Promise.all(
					items
						.filter((fileRef) => fileRef.name !== SHAREABLE_PAGE_NAME)
						.map(async (fileRef) => {
							const url = await fileRef.getDownloadURL();
							return {
								name: fileRef.name,
								url,
							};
						}),
				);
				await downloadFiles(files, onProgress, signal);
			} catch (error) {
				throw new Error(`Failed to download files from folder: ${error.message}`);
			}
		},
		[ref, downloadFiles],
	);

	const getShareableUrl = useCallback(
		async (folderId) => {
			const folderRef = ref.child(folderId);
			const { items } = await folderRef.listAll();
			const files = await Promise.all(
				items
					.filter((fileRef) => fileRef.name !== SHAREABLE_PAGE_NAME)
					.map(async (fileRef) => ({
						name: fileRef.name,
						url: await fileRef.getDownloadURL(),
					})),
			);

			if (files.length === 1) {
				return files[0].url;
			}

			const html = generateSharePageHtml(files);
			const blob = new Blob([html], { type: "text/html" });
			const file = new File([blob], SHAREABLE_PAGE_NAME, { type: "text/html" });
			const result = await putFile(`${folderId}/external-share.html`, file);
			return result.url;
		},
		[putFile],
	);

	return useMemo(
		() => ({
			ref,
			write,
			putFile,
			putFiles,
			unzipAndPutFiles,
			deleteFile,
			deleteFiles,
			downloadFile,
			downloadFiles,
			downloadFilesAsZip,
			downloadFolder,
			getShareableUrl,
			deleteFolder,
		}),
		[
			ref,
			write,
			putFile,
			putFiles,
			unzipAndPutFiles,
			deleteFile,
			deleteFiles,
			downloadFile,
			downloadFiles,
			downloadFilesAsZip,
			downloadFolder,
			getShareableUrl,
			deleteFolder,
		],
	);
}
